import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  prepareRequest,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, RadioSelect, Select } from '@atlas/ui';

import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

import { displayWithdrawalSuccessNotification } from '../withdrawalUtils';

import { WithdrawalBitcoinPopupInfoLabel } from './WithdrawalBitcoinPopupInfoLabel';

const optionsRadioSelect = [{ value: true }, { value: false }];

export const WithdrawalBitcoinPopup = ({ data: { paymentMethod, redirectUrl }, id }) => {
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavedAddress, setIsSavedAddress] = useState(true);
  const [savedAddress, setSavedAddress] = useState();
  const [newAddress, setNewAddress] = useState();
  const {
    addresses,
    amount,
    currencyFrom,
    currencyTo,
    exchangeAmount,
    exchangeRate,
    extTxId,
    feeAmount,
    feeCurrency,
    pmName,
  } = parseWithdrawalQuery(redirectUrl) || {};

  const address = useMemo(
    () => (addresses && isSavedAddress ? savedAddress : newAddress),
    [addresses, isSavedAddress, savedAddress, newAddress],
  );
  const inputLabel = useMemo(() => (addresses ? 'new_address' : 'address'), [addresses]);
  const optionsSelect = useMemo(() => {
    const addressesArray = Array.isArray(addresses) ? addresses : [addresses];
    return addressesArray?.map(item => ({ text: item, value: item }));
  }, [addresses]);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleChangeAddresses = useCallback(option => setIsSavedAddress(option.value), []);
  const handleChangeInput = useCallback(data => setNewAddress(data.target.value.trim()), []);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: 'payments/coinspaid/withdraw',
    });

    setIsSubmitting(true);

    try {
      const response = await request({
        amount,
        cardToken: address,
        cryptoCurrency: currencyTo,
        extPaymentId: extTxId,
        fiatCurrency: currencyFrom,
        pmName,
      });
      displayWithdrawalSuccessNotification({
        amount,
        currency: currencyFrom,
        paymentMethod,
        response,
      });
    } catch (error) {
      NotificationManager.notify({
        message: 'withdrawal_error_message',
        status: 'error',
        type: 'notification',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    amount,
    address,
    currencyTo,
    extTxId,
    currencyFrom,
    pmName,
    paymentMethod,
    handleClose,
  ]);

  useEffect(
    () => () => {
      setIsSavedAddress(true);
      setSavedAddress(null);
      setNewAddress(null);
    },
    [],
  );

  const currencyToFormatter = useMemo(
    () => new CryptoFormatter(languageCode, currencyTo),
    [currencyTo, languageCode],
  );

  const feeFormatter = useMemo(
    () => new CryptoFormatter(languageCode, feeCurrency),
    [feeCurrency, languageCode],
  );

  return (
    <DefaultPopup headerTitle="withdrawal" headerTitlePosition="left" onClose={handleClose}>
      <div className="withdrawal-bitcoin__body">
        <div className="withdrawal-bitcoin__payment-method">
          <PaymentMethodItem paymentMethod={paymentMethod} />
        </div>

        {addresses && (
          <>
            <Label message="select_address" />:
          </>
        )}
        <div className="withdrawal-bitcoin__from-wrapper">
          {addresses && (
            <RadioSelect
              className="withdrawal-bitcoin__radio"
              onChange={handleChangeAddresses}
              options={optionsRadioSelect}
              value={isSavedAddress}
            />
          )}

          <div className="withdrawal-bitcoin__addresses">
            {addresses && (
              <Select
                className="withdrawal-bitcoin__select"
                disabled={addresses && !isSavedAddress}
                label="saved_address"
                onChange={setSavedAddress}
                options={optionsSelect}
              />
            )}

            <Input
              className="withdrawal-bitcoin__input"
              disabled={addresses && isSavedAddress}
              label={inputLabel}
              onChange={handleChangeInput}
              value={newAddress}
            />
          </div>
        </div>

        <div className="withdrawal-bitcoin__info">
          <div>
            <WithdrawalBitcoinPopupInfoLabel label="withdrawal_rate" />
            {`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}
          </div>

          <div>
            <WithdrawalBitcoinPopupInfoLabel label="amount" />
            {`${IntlManager.formatMoney(amount)} (≈ ${currencyToFormatter.format(exchangeAmount * 10 ** 9)})`}
          </div>

          {Boolean(feeAmount) && !Number.isNaN(feeAmount) && (
            <div>
              <WithdrawalBitcoinPopupInfoLabel label="fee" />
              {`≈ ${feeFormatter.format(feeAmount * 10 ** 9)}`}
            </div>
          )}

          <div className="withdrawal-bitcoin__total">
            <WithdrawalBitcoinPopupInfoLabel label="total_amount" />
            {`≈ ${currencyToFormatter.format((exchangeAmount - feeAmount) * 10 ** 9)}`}
          </div>
        </div>

        <Button
          className="withdrawal-bitcoin__button"
          disabled={!address || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalBitcoinPopup.propTypes = {
  data: PropTypes.shape({
    paymentMethod: PropTypes.string,
    redirectUrl: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
